import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Mail, Phone, MapPin, Linkedin, Twitter, 
  Github, Youtube, ChevronRight, ExternalLink, Shield 
} from 'lucide-react';

function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing' },
      { name: 'Integrations', href: '#integrations' },
      { name: 'API Documentation', href: '#api' },
      { name: 'Release Notes', href: '#releases' }
    ],
    solutions: [
      { name: 'SOX Compliance', href: '#sox' },
      { name: 'Internal Audit', href: '#audit' },
      { name: 'Risk Management', href: '#risk' },
      { name: 'Financial Controls', href: '#controls' },
      { name: 'IT Compliance', href: '#it-compliance' }
    ],
    company: [
      { name: 'About Us', href: '#about' },
      { name: 'Careers', href: '#careers' },
      { name: 'Partners', href: '#partners' },
      { name: 'Press', href: '#press' },
      { name: 'Contact', href: '#contact' }
    ],
    resources: [
      { name: 'Blog', href: '#blog' },
      { name: 'Case Studies', href: '#cases' },
      { name: 'Whitepapers', href: '#whitepapers' },
      { name: 'Webinars', href: '#webinars' },
      { name: 'Help Center', href: '#help' }
    ]
  };

  const socialLinks = [
    { icon: <Linkedin className="h-5 w-5" />, href: '#', label: 'LinkedIn' },
    { icon: <Twitter className="h-5 w-5" />, href: '#', label: 'Twitter' },
    { icon: <Github className="h-5 w-5" />, href: '#', label: 'GitHub' },
    { icon: <Youtube className="h-5 w-5" />, href: '#', label: 'YouTube' }
  ];

  return (
    <footer className="bg-blue-800 text-blue-100">
      {/* Newsletter Section */}
      <div className="border-b border-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="md:flex md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <h3 className="text-2xl font-bold text-white mb-2">
                Stay Updated on Compliance Trends
              </h3>
              <p className="text-blue-200">
                Get the latest insights on SOX compliance and audit best practices.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-4 py-3 bg-blue-700 text-white rounded-lg border border-blue-600 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 min-w-[250px]"
              />
              <button className="px-6 py-3 bg-white text-blue-800 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center">
                Subscribe
                <ChevronRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <span className="text-2xl font-bold text-white" style={{
                fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
                letterSpacing: "0.2em",
                fontWeight: "700",
                textTransform: "uppercase"
              }}>SKEPTICS</span>
            </div>
            <p className="text-blue-200 mb-6 leading-relaxed">
              Transforming compliance from a burden to a competitive advantage with AI-powered 
              audit and risk management solutions.
            </p>
            <div className="space-y-3">
              <div className="flex items-center text-blue-200">
                <Mail className="h-4 w-4 mr-3 text-white" />
                <a href="mailto:contact@skeptics.ai" className="hover:text-white transition-colors">
                  contact@skeptics.ai
                </a>
              </div>
              <div className="flex items-center text-blue-200">
                <Phone className="h-4 w-4 mr-3 text-white" />
                <a href="tel:+1-800-SKEPTICS" className="hover:text-white transition-colors">
                  1-800-SKEPTICS
                </a>
              </div>
              <div className="flex items-start text-blue-200">
                <MapPin className="h-4 w-4 mr-3 text-white mt-1" />
                <span>
                  555 Market Street, Suite 1000<br />
                  San Francisco, CA 94105
                </span>
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-blue-200 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-blue-200 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-blue-200 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-blue-200 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Certifications and Compliance */}
        <div className="mt-12 pt-8 border-t border-blue-700">
          <div className="flex flex-wrap items-center justify-center gap-8 mb-8">
            <div className="text-center">
              <div className="bg-blue-700 rounded-lg p-4 mb-2">
                <Shield className="h-8 w-8 text-white mx-auto" />
              </div>
              <p className="text-xs text-blue-200">SOC 2 Type II</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-700 rounded-lg p-4 mb-2">
                <Shield className="h-8 w-8 text-white mx-auto" />
              </div>
              <p className="text-xs text-blue-200">ISO 27001</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-700 rounded-lg p-4 mb-2">
                <Shield className="h-8 w-8 text-white mx-auto" />
              </div>
              <p className="text-xs text-blue-200">GDPR Compliant</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-700 rounded-lg p-4 mb-2">
                <Shield className="h-8 w-8 text-white mx-auto" />
              </div>
              <p className="text-xs text-blue-200">HIPAA Compliant</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex flex-wrap gap-6 mb-4 md:mb-0">
              <Link to="/privacy" className="text-sm text-blue-200 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-blue-200 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/security" className="text-sm text-blue-200 hover:text-white transition-colors">
                Security
              </Link>
              <Link to="/cookies" className="text-sm text-blue-200 hover:text-white transition-colors">
                Cookie Policy
              </Link>
              <Link to="/sitemap" className="text-sm text-blue-200 hover:text-white transition-colors">
                Sitemap
              </Link>
            </div>
            
            <div className="flex items-center justify-between md:justify-end">
              <div className="flex space-x-4 mr-8">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="text-blue-200 hover:text-white transition-colors"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
              
              <p className="text-sm text-blue-200">
                © {currentYear} SKEPTICS. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;